// Typed client for the document pipeline edge functions.

import { BaseEdgeFnClient } from './BaseEdgeFnClient.ts';

/** One extracted document returned by the operator scan-upload → /scan-extract flow. */
export interface ScanUploadDocument {
  id: string;
  document_type: string;
  reference_number: string | null;
  completeness_status: 'complete' | 'missing_parts' | 'illegible';
  needs_review: boolean;
}

export interface ScanUploadResult {
  jobId: string;
  documents: ScanUploadDocument[];
}

export class PipelineEdgeFnClient extends BaseEdgeFnClient {
  /** Re-queue a document job for processing. */
  async rerunJob(jobId: string): Promise<void> {
    await this.callOrThrow<void>('rerun-document-job', { jobId });
  }

  /** Upload an operator scan (PDF/photo) for Gemini extraction. referenceNumber is
   *  optional — when omitted the pipeline matches each document by its own extracted
   *  content. documentStatus threads the upload context ('copy' | 'original') so the
   *  processed scan is filed into the matching docs column; omitted → edge defaults to
   *  'copy'.
   *
   *  P3 route policy (`opts`, all optional):
   *  - `declaredType` skips layout classification. `ie599`/`ie529` route to the
   *    whole-file extraction profile (no split, no crop); `waste_image` skips Gemini
   *    entirely and files the photo straight to the transaction.
   *  - `transactionId` preassigns the target transaction. REQUIRED by the edge fn when
   *    declaredType is `waste_image` (photos never enter the matcher). */
  async uploadScan(
    referenceNumber: string | undefined,
    file: File | Blob,
    documentStatus?: 'copy' | 'original',
    opts?: { declaredType?: string; transactionId?: number },
  ): Promise<ScanUploadResult> {
    const payload: Record<string, unknown> = { action: 'scan_upload' };
    if (referenceNumber) payload.referenceNumber = referenceNumber;
    if (documentStatus) payload.document_status = documentStatus;
    if (opts?.declaredType) payload.declared_type = opts.declaredType;
    if (opts?.transactionId != null) payload.transaction_id = opts.transactionId;
    return this.callWithFiles<ScanUploadResult>(
      'scan-upload',
      payload,
      { file },
    );
  }
}
